
import React, { useState } from 'react';
import { ProjectProfile, Language } from '../types';
import { TRANSLATIONS, getCategoryLabel } from '../translations';
import { X, Check, CheckCircle, Wallet, Globe, ArrowLeft, RefreshCw } from 'lucide-react';

interface KolGrabModeProps {
  projects: ProjectProfile[];
  language: Language;
  onApply: (project: ProjectProfile) => void;
  onBackToList: () => void;
}

export const KolGrabMode: React.FC<KolGrabModeProps> = ({ projects, language, onApply, onBackToList }) => {
  const t = TRANSLATIONS[language];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);

  // Simple filter for demo: only show projects with active campaigns
  const activeProjects = projects.filter(p => p.activeCampaigns > 0);
  const currentProject = activeProjects[currentIndex];

  const handleSwipe = (dir: 'left' | 'right') => {
    setDirection(dir);
    
    setTimeout(() => {
        if (dir === 'right' && currentProject) {
            onApply(currentProject);
        }
        setCurrentIndex(prev => prev + 1);
        setDirection(null);
    }, 300); // Wait for animation
  };

  if (!currentProject) {
      return (
          <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle size={32} className="text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{t.noMoreProjects}</h2>
              <p className="text-slate-400 mb-6">{t.checkBackLater}</p>
              <button onClick={() => setCurrentIndex(0)} className="flex items-center gap-2 px-4 py-2 bg-slate-700 rounded-lg text-white hover:bg-slate-600">
                  <RefreshCw size={16} /> Restart Demo
              </button>
              <button onClick={onBackToList} className="mt-4 text-indigo-400 text-sm">
                  {t.listMode}
              </button>
          </div>
      );
  }

  return (
    <div className="max-w-md mx-auto h-[75vh] flex flex-col relative">
      <div className="flex justify-between items-center mb-4 px-2">
          <button onClick={onBackToList} className="p-2 rounded-full bg-slate-800 text-slate-400">
              <ArrowLeft size={20} />
          </button>
          <span className="text-slate-500 text-xs font-mono">
              {currentIndex + 1} / {activeProjects.length}
          </span>
      </div>

      <div className={`flex-1 bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden shadow-2xl relative flex flex-col transition-transform duration-300 ${direction === 'left' ? '-translate-x-full rotate-[-10deg] opacity-0' : direction === 'right' ? 'translate-x-full rotate-[10deg] opacity-0' : ''}`}>
          {/* Cover Image Area */}
          <div className="h-48 bg-gradient-to-br from-indigo-900 to-slate-900 relative">
              <img src={currentProject.logo} alt="" className="absolute -bottom-8 left-6 w-20 h-20 rounded-xl border-4 border-slate-800 object-cover shadow-lg" />
              <div className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-slate-700">
                  <span className="text-emerald-400 font-bold text-sm">{currentProject.budgetRange}</span>
              </div>
          </div>

          <div className="mt-10 px-6 pb-6 flex-1 flex flex-col">
              <div className="mb-4">
                  <h2 className="text-2xl font-bold text-white leading-tight mb-1">{currentProject.name}</h2>
                  <div className="flex items-center gap-2 text-indigo-400 text-sm font-medium">
                      <span>{getCategoryLabel(currentProject.industry, language)}</span>
                      {currentProject.verified && <CheckCircle size={14} className="text-blue-500" />}
                  </div>
              </div>

              <p className="text-slate-300 text-sm leading-relaxed mb-4 flex-1 overflow-y-auto">
                  {currentProject.description}
              </p>

              <div className="flex flex-wrap gap-2 mb-6">
                  {currentProject.tags.map(tag => (
                      <span key={tag} className="text-xs px-2 py-1 bg-slate-900 border border-slate-700 rounded text-slate-400">#{tag}</span>
                  ))}
              </div>

              <div className="bg-slate-900/50 p-3 rounded-xl border border-slate-700/50 mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-slate-500" />
                      <span className="text-xs text-slate-400">Escrow Secured</span>
                  </div>
                  <a href={currentProject.website} target="_blank" className="flex items-center gap-1 text-xs text-indigo-400 hover:underline">
                      <Globe size={12} /> Website
                  </a>
              </div>
          </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-6 mt-6 pb-4">
          <button 
            onClick={() => handleSwipe('left')}
            className="w-16 h-16 rounded-full bg-slate-800 border border-slate-700 text-rose-500 shadow-lg flex items-center justify-center hover:bg-rose-500 hover:text-white hover:scale-110 transition-all"
          >
              <X size={32} />
          </button>
          
          <button 
            onClick={() => handleSwipe('right')}
            className="w-16 h-16 rounded-full bg-emerald-600 text-white shadow-lg shadow-emerald-500/30 flex items-center justify-center hover:bg-emerald-500 hover:scale-110 transition-all"
          >
              <Check size={32} strokeWidth={3} />
          </button>
      </div>
    </div>
  );
};
